import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Grid,
  Divider,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Logger } from 'utils/Logger';
import { handleApiError } from 'utils/ErrorHandler';
import { API_BASE_URL } from 'envConfig';

// Country codes data
const countryCodes = [
  { code: '+1', country: 'US/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' },
  { code: '+61', country: 'Australia' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+31', country: 'Netherlands' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+45', country: 'Denmark' },
  { code: '+358', country: 'Finland' },
  { code: '+48', country: 'Poland' },
  { code: '+420', country: 'Czech Republic' },
  { code: '+36', country: 'Hungary' },
  { code: '+43', country: 'Austria' },
  { code: '+41', country: 'Switzerland' },
  { code: '+32', country: 'Belgium' },
  { code: '+351', country: 'Portugal' },
  { code: '+30', country: 'Greece' },
  { code: '+90', country: 'Turkey' },
  { code: '+7', country: 'Russia' },
  { code: '+86', country: 'China' },
  { code: '+81', country: 'Japan' },
  { code: '+82', country: 'South Korea' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+66', country: 'Thailand' },
  { code: '+84', country: 'Vietnam' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+974', country: 'Qatar' },
  { code: '+973', country: 'Bahrain' },
  { code: '+965', country: 'Kuwait' },
  { code: '+968', country: 'Oman' },
  { code: '+972', country: 'Israel' },
  { code: '+20', country: 'Egypt' },
  { code: '+27', country: 'South Africa' },
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenya' },
  { code: '+233', country: 'Ghana' },
  { code: '+256', country: 'Uganda' },
  { code: '+251', country: 'Ethiopia' },
  { code: '+212', country: 'Morocco' },
  { code: '+216', country: 'Tunisia' },
  { code: '+213', country: 'Algeria' },
  { code: '+225', country: 'Ivory Coast' },
  { code: '+237', country: 'Cameroon' },
  { code: '+221', country: 'Senegal' },
  { code: '+235', country: 'Chad' },
  { code: '+236', country: 'Central African Republic' },
  { code: '+238', country: 'Cape Verde' },
  { code: '+240', country: 'Equatorial Guinea' },
  { code: '+241', country: 'Gabon' },
  { code: '+242', country: 'Republic of the Congo' },
  { code: '+243', country: 'Democratic Republic of the Congo' },
  { code: '+244', country: 'Angola' },
  { code: '+245', country: 'Guinea-Bissau' },
  { code: '+246', country: 'British Indian Ocean Territory' },
  { code: '+247', country: 'Ascension Island' },
  { code: '+248', country: 'Seychelles' },
  { code: '+249', country: 'Sudan' },
  { code: '+250', country: 'Rwanda' },
  { code: '+252', country: 'Somalia' },
  { code: '+253', country: 'Djibouti' },
  { code: '+255', country: 'Tanzania' },
  { code: '+257', country: 'Burundi' },
  { code: '+258', country: 'Mozambique' },
  { code: '+259', country: 'Zanzibar' },
  { code: '+260', country: 'Zambia' },
  { code: '+261', country: 'Madagascar' },
  { code: '+262', country: 'Réunion' },
  { code: '+263', country: 'Zimbabwe' },
  { code: '+264', country: 'Namibia' },
  { code: '+265', country: 'Malawi' },
  { code: '+266', country: 'Lesotho' },
  { code: '+267', country: 'Botswana' },
  { code: '+268', country: 'Swaziland' },
  { code: '+269', country: 'Comoros' },
  { code: '+290', country: 'Saint Helena' },
  { code: '+291', country: 'Eritrea' },
  { code: '+297', country: 'Aruba' },
  { code: '+298', country: 'Faroe Islands' },
  { code: '+299', country: 'Greenland' },
  { code: '+350', country: 'Gibraltar' },
  { code: '+352', country: 'Luxembourg' },
  { code: '+353', country: 'Ireland' },
  { code: '+354', country: 'Iceland' },
  { code: '+355', country: 'Albania' },
  { code: '+356', country: 'Malta' },
  { code: '+357', country: 'Cyprus' },
  { code: '+359', country: 'Bulgaria' },
  { code: '+370', country: 'Lithuania' },
  { code: '+371', country: 'Latvia' },
  { code: '+372', country: 'Estonia' },
  { code: '+373', country: 'Moldova' },
  { code: '+374', country: 'Armenia' },
  { code: '+375', country: 'Belarus' },
  { code: '+376', country: 'Andorra' },
  { code: '+377', country: 'Monaco' },
  { code: '+378', country: 'San Marino' },
  { code: '+379', country: 'Vatican City' },
  { code: '+380', country: 'Ukraine' },
  { code: '+381', country: 'Serbia' },
  { code: '+382', country: 'Montenegro' },
  { code: '+383', country: 'Kosovo' },
  { code: '+385', country: 'Croatia' },
  { code: '+386', country: 'Slovenia' },
  { code: '+387', country: 'Bosnia and Herzegovina' },
  { code: '+389', country: 'North Macedonia' },
  { code: '+421', country: 'Slovakia' },
  { code: '+423', country: 'Liechtenstein' },
  { code: '+500', country: 'Falkland Islands' },
  { code: '+501', country: 'Belize' },
  { code: '+502', country: 'Guatemala' },
  { code: '+503', country: 'El Salvador' },
  { code: '+504', country: 'Honduras' },
  { code: '+505', country: 'Nicaragua' },
  { code: '+506', country: 'Costa Rica' },
  { code: '+507', country: 'Panama' },
  { code: '+508', country: 'Saint Pierre and Miquelon' },
  { code: '+509', country: 'Haiti' },
  { code: '+590', country: 'Guadeloupe' },
  { code: '+591', country: 'Bolivia' },
  { code: '+592', country: 'Guyana' },
  { code: '+593', country: 'Ecuador' },
  { code: '+594', country: 'French Guiana' },
  { code: '+595', country: 'Paraguay' },
  { code: '+596', country: 'Martinique' },
  { code: '+597', country: 'Suriname' },
  { code: '+598', country: 'Uruguay' },
  { code: '+599', country: 'Netherlands Antilles' },
  { code: '+670', country: 'East Timor' },
  { code: '+672', country: 'Australian External Territories' },
  { code: '+673', country: 'Brunei' },
  { code: '+674', country: 'Nauru' },
  { code: '+675', country: 'Papua New Guinea' },
  { code: '+676', country: 'Tonga' },
  { code: '+677', country: 'Solomon Islands' },
  { code: '+678', country: 'Vanuatu' },
  { code: '+679', country: 'Fiji' },
  { code: '+680', country: 'Palau' },
  { code: '+681', country: 'Wallis and Futuna' },
  { code: '+682', country: 'Cook Islands' },
  { code: '+683', country: 'Niue' },
  { code: '+685', country: 'Samoa' },
  { code: '+686', country: 'Kiribati' },
  { code: '+687', country: 'New Caledonia' },
  { code: '+688', country: 'Tuvalu' },
  { code: '+689', country: 'French Polynesia' },
  { code: '+690', country: 'Tokelau' },
  { code: '+691', country: 'Micronesia' },
  { code: '+692', country: 'Marshall Islands' },
  { code: '+850', country: 'North Korea' },
  { code: '+852', country: 'Hong Kong' },
  { code: '+853', country: 'Macau' },
  { code: '+855', country: 'Cambodia' },
  { code: '+856', country: 'Laos' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+886', country: 'Taiwan' },
  { code: '+960', country: 'Maldives' },
  { code: '+961', country: 'Lebanon' },
  { code: '+962', country: 'Jordan' },
  { code: '+963', country: 'Syria' },
  { code: '+964', country: 'Iraq' },
  { code: '+967', country: 'Yemen' },
  { code: '+970', country: 'Palestine' },
  { code: '+975', country: 'Bhutan' },
  { code: '+976', country: 'Mongolia' },
  { code: '+977', country: 'Nepal' },
  { code: '+992', country: 'Tajikistan' },
  { code: '+993', country: 'Turkmenistan' },
  { code: '+994', country: 'Azerbaijan' },
  { code: '+995', country: 'Georgia' },
  { code: '+996', country: 'Kyrgyzstan' },
  { code: '+998', country: 'Uzbekistan' },
  { code: '+1242', country: 'Bahamas' },
  { code: '+1246', country: 'Barbados' },
  { code: '+1264', country: 'Anguilla' },
  { code: '+1268', country: 'Antigua and Barbuda' },
  { code: '+1284', country: 'British Virgin Islands' },
  { code: '+1340', country: 'U.S. Virgin Islands' },
  { code: '+1345', country: 'Cayman Islands' },
  { code: '+1441', country: 'Bermuda' },
  { code: '+1473', country: 'Grenada' },
  { code: '+1649', country: 'Turks and Caicos Islands' },
  { code: '+1664', country: 'Montserrat' },
  { code: '+1671', country: 'Guam' },
  { code: '+1684', country: 'American Samoa' },
  { code: '+1758', country: 'Saint Lucia' },
  { code: '+1767', country: 'Dominica' },
  { code: '+1784', country: 'Saint Vincent and the Grenadines' },
  { code: '+1809', country: 'Dominican Republic' },
  { code: '+1868', country: 'Trinidad and Tobago' },
  { code: '+1869', country: 'Saint Kitts and Nevis' },
  { code: '+1876', country: 'Jamaica' },
];

// Browser info utility
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const browserInfo = {
    userAgent: userAgent,
    browserName: '',
    browserVersion: '',
    os: '',
    device: '',
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
    screenResolution: `${screen.width}x${screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: screen.colorDepth,
    cookieEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
    platform: navigator.platform,
  };

  // Detect browser
  if (userAgent.includes('Chrome')) {
    browserInfo.browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserInfo.browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Firefox')) {
    browserInfo.browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserInfo.browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Safari')) {
    browserInfo.browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserInfo.browserVersion = match ? match[1] : '';
  } else if (userAgent.includes('Edge')) {
    browserInfo.browserName = 'Edge';
    const match = userAgent.match(/Edge\/(\d+)/);
    browserInfo.browserVersion = match ? match[1] : '';
  } else {
    browserInfo.browserName = 'Unknown';
  }

  // Detect OS
  if (userAgent.includes('Windows')) {
    browserInfo.os = 'Windows';
  } else if (userAgent.includes('Mac')) {
    browserInfo.os = 'macOS';
  } else if (userAgent.includes('Linux')) {
    browserInfo.os = 'Linux';
  } else if (userAgent.includes('Android')) {
    browserInfo.os = 'Android';
  } else if (userAgent.includes('iOS')) {
    browserInfo.os = 'iOS';
  } else {
    browserInfo.os = 'Unknown';
  }

  // Detect device
  if (userAgent.includes('Mobile')) {
    browserInfo.device = 'Mobile';
  } else if (userAgent.includes('Tablet')) {
    browserInfo.device = 'Tablet';
  } else {
    browserInfo.device = 'Desktop';
  }

  return browserInfo;
};

// Helper function to set cookie
const setConsentCookie = (companyId: string) => {
  const expiryDate = new Date();
  expiryDate.setMinutes(expiryDate.getMinutes() + 15); // 15 minutes from now
  document.cookie = `consent_submitted_${companyId}=true; expires=${expiryDate.toUTCString()}; path=/`;
};

// Helper function to check if consent was recently submitted
const checkConsentCookie = (companyId: string): boolean => {
  const cookies = document.cookie.split(';');
  const consentCookie = cookies.find(cookie => 
    cookie.trim().startsWith(`consent_submitted_${companyId}=`)
  );
  return !!consentCookie;
};

interface ContactData {
  title: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phoneNumber: string;
  emailAddress: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
}

interface ConsentFormData {
  marketingEmails: boolean;
  marketingWhatsApp: boolean;
  marketingSMS: boolean;
  title: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phoneNumber: string;
  emailAddress: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  emailAddress?: string;
  city?: string;
  country?: string;
}

interface ConsentFormPanelProps {
  dataSetId: string;
  contactId: string;
  companyName: string;
  companyId: string;
}

export const ConsentFormPanel: React.FC<ConsentFormPanelProps> = ({ 
  dataSetId, 
  contactId, 
  companyName,
  companyId
}) => {
  const theme = useTheme();
  const [contactData, setContactData] = useState<ContactData | null>(null);
  const [formData, setFormData] = useState<ConsentFormData>({
    marketingEmails: true,
    marketingWhatsApp: true,
    marketingSMS: true,
    title: '',
    firstName: '',
    lastName: '',
    countryCode: '',
    phoneNumber: '',
    emailAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchContactData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/public/contact/${dataSetId}/${contactId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorMessage = await handleApiError(response);
        throw new Error(`Failed to fetch contact data: ${errorMessage}`);
      }

      const data = await response.json();
      setContactData(data.data);
      
      // Pre-populate form with contact data
      setFormData({
        marketingEmails: true,
        marketingWhatsApp: true,
        marketingSMS: true,
        title: data.data.title || '',
        firstName: data.data.firstName || '',
        lastName: data.data.lastName || '',
        countryCode: data.data.countryCode || '',
        phoneNumber: data.data.phoneNumber || '',
        emailAddress: data.data.emailAddress || '',
        addressLine1: data.data.addressLine1 || '',
        addressLine2: data.data.addressLine2 || '',
        city: data.data.city || '',
        state: data.data.state || '',
        country: data.data.country || '',
      });

      Logger.debug('ConsentFormPanel.tsx', `Contact data fetched successfully for dataSetId: ${dataSetId}, contactId: ${contactId}`);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      Logger.error('ConsentFormPanel.tsx', `Failed to fetch contact data: ${errorMessage}`);
      // Don't block the form - just log the error and continue with empty form
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if consent was recently submitted
    if (checkConsentCookie(companyId)) {
      setIsSubmitted(true);
      setLoading(false);
      return;
    }

    if (dataSetId && contactId && dataSetId.trim() && contactId.trim()) {
      fetchContactData();
    } else {
      // If no valid dataSetId or contactId, set loading to false without making API call
      setLoading(false);
      // Initialize form with empty data when no valid IDs
      setFormData({
        marketingEmails: true,
        marketingWhatsApp: true,
        marketingSMS: true,
        title: '',
        firstName: '',
        lastName: '',
        countryCode: '',
        phoneNumber: '',
        emailAddress: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
      });
    }
  }, [dataSetId, contactId, companyId]);

  const handleInputChange = (field: keyof ConsentFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field.includes('marketing') ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First Name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last Name is required';
    }
    
    if (!formData.countryCode) {
      errors.phoneNumber = 'Country Code is required';
    }
    
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone Number is required';
    } else {
      // Phone number validation: no 0 or + at start, only numbers, 4-13 characters
      const phoneRegex = /^[1-9]\d{3,12}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        errors.phoneNumber = 'Phone number must be 4-13 digits and cannot start with 0 or +';
      }
    }
    
    if (!formData.emailAddress.trim()) {
      errors.emailAddress = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      errors.emailAddress = 'Please enter a valid email address';
    }
    
    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }
    
    if (!formData.country.trim()) {
      errors.country = 'Country is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      Logger.debug('ConsentFormPanel.tsx', 'Form validation failed');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    Logger.debug('ConsentFormPanel.tsx', `Form submitted: ${JSON.stringify(formData)}`);
    
    try {
      // Get browser info
      const browserInfo = getBrowserInfo();
      
      // Concatenate country code and phone number
      const fullPhoneNumber = formData.countryCode + formData.phoneNumber;
      
      const response = await fetch(`${API_BASE_URL}/public/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: companyId,
          title: formData.title,
          firstName: formData.firstName,
          lastName: formData.lastName,
          countryCode: formData.countryCode,
          phoneNumber: formData.phoneNumber,
          fullPhoneNumber: fullPhoneNumber,
          emailAddress: formData.emailAddress,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          marketingEmails: formData.marketingEmails,
          marketingWhatsApp: formData.marketingWhatsApp,
          marketingSMS: formData.marketingSMS,
          browserInfo: browserInfo
        }),
      });

      if (!response.ok) {
        const errorMessage = await handleApiError(response);
        throw new Error(`Failed to submit consent: ${errorMessage}`);
      }

      const data = await response.json();
      Logger.debug('ConsentFormPanel.tsx', `Consent submitted successfully: ${JSON.stringify(data)}`);
      
      // Set cookie and show success state
      setConsentCookie(companyId);
      setIsSubmitted(true);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      Logger.error('ConsentFormPanel.tsx', `Failed to submit consent: ${errorMessage}`);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // Log the error but still show the form without pre-filled data
    Logger.error('ConsentFormPanel.tsx', `API error: ${error}`);
  }

  // Show success state if consent was submitted
  if (isSubmitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '1920px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: '800px',
            borderRadius: 3,
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgb(20, 25, 55)' 
              : theme.palette.grey[100],
            border: `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.12)' 
              : theme.palette.grey[300]}`,
            boxShadow: theme.shadows[2],
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  fontWeight: 'bold',
                  color: theme.palette.success.main,
                  mb: 2,
                }}
              >
                ✅ Consent Submitted Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Thank you for providing your consent. Your preferences have been recorded and will be respected.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can update your consent preferences at any time by contacting the company directly.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1920px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: '800px',
          borderRadius: 3,
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgb(20, 25, 55)' 
            : theme.palette.grey[100],
          border: `1px solid ${theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.12)' 
            : theme.palette.grey[300]}`,
          boxShadow: theme.shadows[2],
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 'bold',
                color: theme.palette.secondary.main,
                mb: 1,
              }}
            >
              Consent Form
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please provide your consent and update your contact information
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            {/* Consent Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Marketing Consent
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.marketingEmails}
                      onChange={handleInputChange('marketingEmails')}
                      color="secondary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: '1.2rem', color: theme.palette.secondary.main }} />
                      <Typography>
                        I consent to receive marketing emails
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.marketingWhatsApp}
                      onChange={handleInputChange('marketingWhatsApp')}
                      color="secondary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsAppIcon sx={{ fontSize: '1.2rem', color: theme.palette.secondary.main }} />
                      <Typography>
                        I consent to receive marketing WhatsApp messages from {companyName}
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.marketingSMS}
                      onChange={handleInputChange('marketingSMS')}
                      color="secondary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmsIcon sx={{ fontSize: '1.2rem', color: theme.palette.secondary.main }} />
                      <Typography>
                        I consent to receive SMS from {companyName}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Personal Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Title"
                    value={formData.title}
                    onChange={handleInputChange('title')}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="Mr">Mr</MenuItem>
                    <MenuItem value="Mrs">Mrs</MenuItem>
                    <MenuItem value="Ms">Ms</MenuItem>
                    <MenuItem value="Dr">Dr</MenuItem>
                    <MenuItem value="Prof">Prof</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={4.5}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    variant="outlined"
                    size="small"
                    required
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4.5}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    variant="outlined"
                    size="small"
                    required
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={countryCodes}
                    getOptionLabel={(option) => `${option.code} ${option.country}`}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={countryCodes.find(code => code.code === formData.countryCode) || null}
                    onChange={(_, newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        countryCode: newValue?.code || ''
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country Code *"
                        variant="outlined"
                        size="small"
                        required
                        error={!!formErrors.phoneNumber}
                        helperText={formErrors.phoneNumber}
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      const filtered = options.filter(option =>
                        option.code.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.country.toLowerCase().includes(inputValue.toLowerCase())
                      );
                      return filtered;
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.code}>
                        {`${option.code} ${option.country}`}
                      </li>
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      // Only allow numbers and prevent 0 or + at start
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value && (value.startsWith('0') || value.startsWith('+'))) {
                        return; // Don't update if starts with 0 or +
                      }
                      setFormData(prev => ({
                        ...prev,
                        phoneNumber: value
                      }));
                    }}
                    variant="outlined"
                    size="small"
                    type="tel"
                    required
                    error={!!formErrors.phoneNumber}
                    helperText={formErrors.phoneNumber}
                    inputProps={{
                      maxLength: 13,
                      minLength: 4
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address *"
                    value={formData.emailAddress}
                    onChange={handleInputChange('emailAddress')}
                    variant="outlined"
                    size="small"
                    type="email"
                    required
                    error={!!formErrors.emailAddress}
                    helperText={formErrors.emailAddress}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={formData.addressLine1}
                    onChange={handleInputChange('addressLine1')}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={formData.addressLine2}
                    onChange={handleInputChange('addressLine2')}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="City *"
                    value={formData.city}
                    onChange={handleInputChange('city')}
                    variant="outlined"
                    size="small"
                    required
                    error={!!formErrors.city}
                    helperText={formErrors.city}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="State / County"
                    value={formData.state}
                    onChange={handleInputChange('state')}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Country *"
                    value={formData.country}
                    onChange={handleInputChange('country')}
                    variant="outlined"
                    size="small"
                    required
                    error={!!formErrors.country}
                    helperText={formErrors.country}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Terms and Conditions Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Terms and Conditions: Use of Personal Data
              </Typography>
              
              <Box sx={{ 
                maxHeight: '400px', 
                overflowY: 'auto', 
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : theme.palette.grey[300]}`,
                borderRadius: 1,
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : theme.palette.grey[50],
              }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>1. Purpose of Data Collection</strong><br />
                  We collect and process personal data solely for the purpose of delivering relevant marketing communications via email, SMS, WhatsApp, or other supported channels. These messages may include promotional content, product updates, event invitations, or offers that we believe may interest you.
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>2. Types of Personal Data Collected</strong><br />
                  We may collect and store the following data:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Typography component="li" variant="body2">Full name</Typography>
                  <Typography component="li" variant="body2">Contact details (email address, phone number, WhatsApp number)</Typography>
                  <Typography component="li" variant="body2">Consent preferences</Typography>
                  <Typography component="li" variant="body2">Interaction history with our marketing campaigns</Typography>
                  <Typography component="li" variant="body2">Geolocation and device metadata (when applicable)</Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>3. Legal Basis for Processing</strong><br />
                  Our processing is based on your explicit consent. You may withdraw this consent at any time via the unsubscribe link, reply options, or dedicated consent portal.<br /><br />
                  Where required, processing may also be based on:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Typography component="li" variant="body2">Fulfilment of a contract</Typography>
                  <Typography component="li" variant="body2">Compliance with a legal obligation</Typography>
                  <Typography component="li" variant="body2">Legitimate interest (e.g. fraud prevention, service improvement)</Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>4. Global Data Protection Compliance</strong><br />
                  We strive to comply with all relevant data protection laws across jurisdictions. This includes:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Typography component="li" variant="body2">GDPR (EU/UK): Lawful, transparent processing with strict rights over access, correction, and deletion.</Typography>
                  <Typography component="li" variant="body2">CCPA (California): Your right to know, delete, and opt-out of sale of personal data.</Typography>
                  <Typography component="li" variant="body2">LGPD (Brazil): Clear purpose limitation and user control over data.</Typography>
                  <Typography component="li" variant="body2">POPIA (South Africa): Lawful and responsible data handling with disclosure requirements.</Typography>
                  <Typography component="li" variant="body2">PIPEDA (Canada): Fair information practices including safeguarding personal data.</Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>5. Cross-Border Data Transfers</strong><br />
                  Your data may be transferred and stored in countries outside your local jurisdiction. Where this occurs, we ensure data protection levels equivalent to local law through:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Typography component="li" variant="body2">Standard contractual clauses</Typography>
                  <Typography component="li" variant="body2">Adequacy decisions (where applicable)</Typography>
                  <Typography component="li" variant="body2">Additional security and encryption measures</Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>6. Third-Party Processing</strong><br />
                  We may share your data with trusted service providers who help us deliver our messaging services. All partners are contractually obligated to protect your data and process it only on our behalf for limited purposes.
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>6.1. Campaign King Data Processing</strong><br />
                  Campaign King acts as a data processor on behalf of our clients (data controllers). We store and process consent information and personal data solely for the purpose of providing our messaging services to our clients. Campaign King will not use your personal data for its own marketing purposes, product development, or any other commercial activities unrelated to the provision of our contracted services. We process your data only as instructed by our clients and in accordance with applicable data protection laws.
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>7. Data Retention</strong><br />
                  Your data will be retained only for as long as necessary to fulfill the purposes described above, or as required by law. Inactive or unsubscribed data will be deleted or anonymized after a reasonable retention period.
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>8. User Rights</strong><br />
                  You may exercise your rights at any time, including:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Typography component="li" variant="body2">Access to your personal data</Typography>
                  <Typography component="li" variant="body2">Correction of inaccurate information</Typography>
                  <Typography component="li" variant="body2">Erasure (right to be forgotten)</Typography>
                  <Typography component="li" variant="body2">Objection to processing</Typography>
                  <Typography component="li" variant="body2">Data portability (in applicable jurisdictions)</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  To initiate a request, please contact our Data Protection Officer at [Insert Contact].
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>9. Consent Management</strong><br />
                  You can manage your preferences via our Consent Portal. Changes to your consent status will be honored promptly.
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>10. Policy Updates</strong><br />
                  These terms may be updated to reflect legal changes or service improvements. Users will be notified of significant updates and encouraged to review them.
                </Typography>
              </Box>
            </Box>

            {/* Error Message */}
            {submitError && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              </Box>
            )}

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                size="large"
                disabled={isSubmitting}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'none',
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Submitting...
                  </>
                ) : (
                  'Submit Consent'
                )}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}; 